import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, Avatar, Badge, Spin, App as AntdApp, Empty, Dropdown, Modal, Form, Select, Popover, Upload, Tooltip } from 'antd';
import type { UploadFile } from 'antd';
import { Send, Plus, Users, MoreVertical, Search, MessageCircle, ArrowLeft, UserPlus, Trash2, LogOut, Smile, Paperclip, X, Download, Reply, SearchCheck, Pin, PinOff, ChevronDown, ChevronUp } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { chatService, type ChatRoom, type ChatMessage } from '@/services/chatService';
import { useChatHub } from '@/hooks/useChatHub';
import { useAuthStore } from '@/store/authStore';
import { userService } from '@/services/userService';
import { formatDistanceToNow, formatShortDateTime } from '@/utils/date';

// Emoji đơn giản - top dùng nhiều nhất
const EMOJI_PICKS = ['😀','😂','🤣','😊','😍','😘','🥰','😎','🤔','😢','😭','😡','👍','👎','👌','🙏','👏','💪','🎉','🔥','❤️','💔','💯','✅','❌','⚠️','📌','📎','📧','🏥','💊','🩺','👨‍⚕️','👩‍⚕️','📋','📝','💼','⏰','📅'];

export function ChatPage() {
  const { id: activeRoomId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { message } = AntdApp.useApp();
  const currentUser = useAuthStore((s) => s.user);

  const [search, setSearch] = useState('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState<'direct' | 'group' | null>(null);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // === Queries ===
  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ['chat-rooms'],
    queryFn: chatService.listRooms,
    refetchInterval: 30000,
  });

  const { data: messages = [], isLoading: messagesLoading } = useQuery({
    queryKey: ['chat-messages', activeRoomId],
    queryFn: () => activeRoomId ? chatService.listMessages(activeRoomId) : Promise.resolve([]),
    enabled: !!activeRoomId,
  });

  const { data: searchResults = [], isLoading: searchLoading } = useQuery({
    queryKey: ['chat-search', activeRoomId, messageSearchQuery],
    queryFn: () => activeRoomId && messageSearchQuery.trim()
      ? chatService.searchMessages(activeRoomId, messageSearchQuery.trim())
      : Promise.resolve([]),
    enabled: !!activeRoomId && messageSearchQuery.trim().length > 0,
  });

  const { data: pinnedMessages = [] } = useQuery({
    queryKey: ['chat-pinned', activeRoomId],
    queryFn: () => activeRoomId ? chatService.listPinned(activeRoomId) : Promise.resolve([]),
    enabled: !!activeRoomId,
  });

  const [pinnedExpanded, setPinnedExpanded] = useState(false);

  // Mobile detection: dưới 768px = mobile view
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const activeRoom = useMemo(() => rooms.find(r => r.id === activeRoomId), [rooms, activeRoomId]);

  // === Realtime: chỉ còn lo update messages cache ở phòng đang mở.
  // Notification + refresh badge đã được handle bởi ChatNotifier global ở MainLayout
  useChatHub({
    onMessageReceived: (msg) => {
      // Append vào cache messages nếu đang mở đúng phòng
      if (msg.roomId === activeRoomId) {
        qc.setQueryData<ChatMessage[]>(['chat-messages', activeRoomId], (old = []) => {
          if (old.some(m => m.id === msg.id)) return old;
          return [...old, msg];
        });
      }
    },
    onRoomUpdated: () => {
      qc.invalidateQueries({ queryKey: ['chat-rooms'] });
      qc.invalidateQueries({ queryKey: ['chat-pinned', activeRoomId] });
    },
  });

  // === Effects ===
  // Mark read khi mở phòng
  useEffect(() => {
    if (activeRoomId) {
      chatService.markRead(activeRoomId).then(() => {
        qc.invalidateQueries({ queryKey: ['chat-rooms'] });
      }).catch(() => {});
    }
    // Đổi phòng → clear search state
    setSearchPanelOpen(false);
    setMessageSearchQuery('');
    setHighlightMessageId(null);
  }, [activeRoomId, qc]);

  // Scroll xuống cuối khi có tin mới (nhưng không phải khi đang highlight search result)
  useEffect(() => {
    if (!highlightMessageId) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, highlightMessageId]);

  // Scroll tới tin được highlight từ search
  useEffect(() => {
    if (highlightMessageId) {
      const el = messageRefs.current[highlightMessageId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Bỏ highlight sau 2.5s
        const t = setTimeout(() => setHighlightMessageId(null), 2500);
        return () => clearTimeout(t);
      }
    }
  }, [highlightMessageId, messages]);

  // === Handlers ===
  const handleSend = async () => {
    if (!input.trim() || !activeRoomId) return;
    // Parse @mention từ content: tìm pattern @[fullName](userId)
    const mentionedIds: string[] = [];
    const mentionRegex = /@\[[^\]]+\]\(([a-f0-9-]+)\)/g;
    let m;
    while ((m = mentionRegex.exec(input)) !== null) {
      mentionedIds.push(m[1]);
    }
    // Cũng parse dạng @fullName đơn giản → match với members trong phòng
    if (activeRoom) {
      const simpleMentions = input.match(/@(\w+[\wÀ-ỹ]*)/g) || [];
      for (const sm of simpleMentions) {
        const name = sm.slice(1).trim();
        const member = activeRoom.members.find(m =>
          m.fullName.toLowerCase().includes(name.toLowerCase())
        );
        if (member && !mentionedIds.includes(member.userId)) {
          mentionedIds.push(member.userId);
        }
      }
    }

    setSending(true);
    try {
      const msg = await chatService.sendMessage(activeRoomId, {
        content: input.trim(),
        replyToId: replyTo?.id,
        mentionedIds: mentionedIds.length > 0 ? mentionedIds : undefined,
      });
      qc.setQueryData<ChatMessage[]>(['chat-messages', activeRoomId], (old = []) => {
        if (old.some(m => m.id === msg.id)) return old;
        return [...old, msg];
      });
      setInput('');
      setReplyTo(null);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Gửi tin thất bại');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!activeRoomId) return;
    if (file.size > 20 * 1024 * 1024) {
      message.error('File vượt quá 20MB');
      return;
    }
    setUploading(true);
    try {
      const msg = await chatService.sendAttachment(activeRoomId, file, input.trim() || undefined);
      qc.setQueryData<ChatMessage[]>(['chat-messages', activeRoomId], (old = []) => {
        if (old.some(m => m.id === msg.id)) return old;
        return [...old, msg];
      });
      setInput('');
      message.success(`Đã gửi ${file.name}`);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Upload thất bại');
    } finally {
      setUploading(false);
    }
  };

  const handleInsertEmoji = (emoji: string) => {
    setInput(prev => prev + emoji);
  };

  const handlePin = async (messageId: string) => {
    if (!activeRoomId) return;
    try {
      await chatService.pinMessage(messageId);
      qc.invalidateQueries({ queryKey: ['chat-pinned', activeRoomId] });
      qc.invalidateQueries({ queryKey: ['chat-messages', activeRoomId] });
      message.success('Đã ghim tin nhắn');
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Không ghim được');
    }
  };

  const handleUnpin = async (messageId: string) => {
    if (!activeRoomId) return;
    try {
      await chatService.unpinMessage(messageId);
      qc.invalidateQueries({ queryKey: ['chat-pinned', activeRoomId] });
      qc.invalidateQueries({ queryKey: ['chat-messages', activeRoomId] });
      message.success('Đã gỡ ghim');
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Không gỡ ghim được');
    }
  };

  const filteredRooms = useMemo(() => {
    if (!search.trim()) return rooms;
    const q = search.toLowerCase();
    return rooms.filter(r => {
      const name = getRoomDisplayName(r, currentUser?.id).toLowerCase();
      return name.includes(q) || r.lastMessagePreview?.toLowerCase().includes(q);
    });
  }, [rooms, search, currentUser?.id]);

  // === Render ===
  return (
    <>
      <PageHeader title="Tin nhắn" subtitle="Trao đổi nội bộ với đồng nghiệp" />

      <div style={{
        display: 'flex',
        height: isMobile ? 'calc(100vh - 160px)' : 'calc(100vh - 220px)',
        minHeight: 500,
        background: '#fff',
        border: '1px solid #e1e5eb',
        borderRadius: 8,
        overflow: 'hidden',
      }}>
        {/* ========== SIDEBAR LIST ROOMS ========== */}
        {/* Mobile: ẩn sidebar khi đang mở 1 room */}
        <div style={{
          width: isMobile ? '100%' : 320,
          display: isMobile && activeRoomId ? 'none' : 'flex',
          borderRight: isMobile ? 'none' : '1px solid #e1e5eb',
          flexDirection: 'column',
          background: '#fafbfc',
        }}>
          {/* Header sidebar */}
          <div style={{ padding: 12, borderBottom: '1px solid #e1e5eb' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Input
                prefix={<Search size={14} style={{ color: '#8a95a5' }} />}
                placeholder="Tìm kiếm..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="middle"
                allowClear
              />
              <Dropdown
                menu={{
                  items: [
                    { key: 'direct', label: 'Chat 1-1', icon: <MessageCircle size={14} />, onClick: () => setCreateModalOpen('direct') },
                    { key: 'group', label: 'Tạo nhóm', icon: <Users size={14} />, onClick: () => setCreateModalOpen('group') },
                  ],
                }}
                trigger={['click']}
              >
                <Button type="primary" icon={<Plus size={14} />} />
              </Dropdown>
            </div>
          </div>

          {/* List rooms */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {roomsLoading ? (
              <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
            ) : filteredRooms.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#8a95a5', fontSize: 12 }}>
                {search ? 'Không tìm thấy' : 'Chưa có cuộc trò chuyện. Bấm + để bắt đầu.'}
              </div>
            ) : (
              filteredRooms.map(room => (
                <RoomItem
                  key={room.id}
                  room={room}
                  active={room.id === activeRoomId}
                  currentUserId={currentUser?.id}
                  onClick={() => navigate(`/chat/${room.id}`)}
                />
              ))
            )}
          </div>
        </div>

        {/* ========== CONVERSATION + SEARCH PANEL ========== */}
        {/* Mobile: ẩn khi chưa có activeRoomId */}
        <div style={{
          flex: 1,
          display: isMobile && !activeRoomId ? 'none' : 'flex',
          minWidth: 0,
        }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!activeRoom ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Empty
                image={<MessageCircle size={64} style={{ color: '#d4d9df' }} />}
                imageStyle={{ height: 80 }}
                description={<span style={{ color: '#8a95a5' }}>Chọn cuộc trò chuyện để bắt đầu</span>}
              />
            </div>
          ) : (
            <>
              {/* Header phòng */}
              <ChatHeader
                room={activeRoom}
                currentUserId={currentUser?.id}
                onBack={() => navigate('/chat')}
                onToggleSearch={() => setSearchPanelOpen(v => !v)}
                searchActive={searchPanelOpen}
              />

              {/* Pinned messages banner */}
              {pinnedMessages.length > 0 && (
                <PinnedBanner
                  messages={pinnedMessages}
                  expanded={pinnedExpanded}
                  onToggle={() => setPinnedExpanded(v => !v)}
                  currentUserId={currentUser?.id}
                  roomType={activeRoom.type}
                  myRole={activeRoom.members.find(m => m.userId === currentUser?.id)?.role}
                  onJumpTo={(id) => {
                    const exists = messages.some(m => m.id === id);
                    if (exists) {
                      setHighlightMessageId(id);
                    } else {
                      message.info('Tin này ngoài phạm vi 50 tin gần nhất. Cuộn lên để xem.');
                    }
                  }}
                  onUnpin={handleUnpin}
                />
              )}

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f8f9fa' }}>
                {messagesLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : messages.length === 0 ? (
                  <Empty description="Chưa có tin nhắn" />
                ) : (
                  messages.map((msg, idx) => (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      isMine={msg.senderId === currentUser?.id}
                      showAvatar={idx === 0 || messages[idx - 1].senderId !== msg.senderId}
                      onReply={() => setReplyTo(msg)}
                      onPin={() => handlePin(msg.id)}
                      onUnpin={() => handleUnpin(msg.id)}
                      canPin={canPinMessage(msg, currentUser?.id, activeRoom)}
                      highlighted={highlightMessageId === msg.id}
                      innerRef={(el) => { messageRefs.current[msg.id] = el; }}
                    />
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply preview */}
              {replyTo && (
                <div style={{
                  padding: '8px 12px',
                  background: '#f0f3f7',
                  borderTop: '1px solid #e1e5eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <Reply size={14} style={{ color: '#6699CC' }} />
                  <div style={{ flex: 1, fontSize: 12, minWidth: 0 }}>
                    <div style={{ color: '#6699CC', fontWeight: 600 }}>
                      Trả lời {replyTo.senderName}
                    </div>
                    <div style={{
                      color: '#7d8a98',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {replyTo.content}
                    </div>
                  </div>
                  <Button type="text" size="small" icon={<X size={14} />} onClick={() => setReplyTo(null)} />
                </div>
              )}

              {/* Input */}
              <div style={{ padding: 12, borderTop: '1px solid #e1e5eb', background: '#fff' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                  {/* Emoji picker */}
                  <Popover
                    trigger="click"
                    placement="topLeft"
                    content={
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 1fr)',
                        gap: 4,
                        maxWidth: 280,
                      }}>
                        {EMOJI_PICKS.map(e => (
                          <button
                            key={e}
                            onClick={() => handleInsertEmoji(e)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              fontSize: 20,
                              padding: 4,
                              borderRadius: 4,
                            }}
                            onMouseEnter={(ev) => (ev.currentTarget.style.background = '#f0f3f7')}
                            onMouseLeave={(ev) => (ev.currentTarget.style.background = 'transparent')}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    }
                  >
                    <Tooltip title="Emoji">
                      <Button type="text" icon={<Smile size={18} />} />
                    </Tooltip>
                  </Popover>

                  {/* File upload */}
                  <Tooltip title="Đính kèm file (tối đa 20MB)">
                    <Button
                      type="text"
                      icon={<Paperclip size={18} />}
                      loading={uploading}
                      onClick={() => fileInputRef.current?.click()}
                    />
                  </Tooltip>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload(f);
                      e.target.value = '';
                    }}
                  />

                  {/* Text area với @mention detect */}
                  <div style={{ flex: 1, position: 'relative' }}>
                    <Input.TextArea
                      value={input}
                      onChange={(e) => {
                        setInput(e.target.value);
                        // Detect @ để hiện mention dropdown
                        const val = e.target.value;
                        const lastAt = val.lastIndexOf('@');
                        if (lastAt >= 0 && (lastAt === 0 || val[lastAt - 1] === ' ')) {
                          const afterAt = val.slice(lastAt + 1);
                          if (!afterAt.includes(' ')) {
                            setMentionQuery(afterAt);
                          } else {
                            setMentionQuery(null);
                          }
                        } else {
                          setMentionQuery(null);
                        }
                      }}
                      placeholder="Nhập tin nhắn... (@ để nhắc tên, Enter gửi, Shift+Enter xuống dòng)"
                      autoSize={{ minRows: 1, maxRows: 5 }}
                      onPressEnter={(e) => {
                        if (!e.shiftKey && !mentionQuery) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                    />
                    {/* @mention dropdown */}
                    {mentionQuery !== null && activeRoom && (
                      <MentionDropdown
                        query={mentionQuery}
                        members={activeRoom.members.filter(m => m.userId !== currentUser?.id)}
                        onPick={(member) => {
                          const lastAt = input.lastIndexOf('@');
                          const before = input.slice(0, lastAt);
                          setInput(before + `@${member.fullName} `);
                          setMentionQuery(null);
                        }}
                      />
                    )}
                  </div>

                  <Button
                    type="primary"
                    icon={<Send size={14} />}
                    loading={sending}
                    disabled={!input.trim()}
                    onClick={handleSend}
                  >
                    Gửi
                  </Button>
                </div>
              </div>
            </>
          )}
          </div>

          {/* ========== SEARCH PANEL ========== */}
          {activeRoom && searchPanelOpen && (
            <div style={{
              width: 320,
              borderLeft: '1px solid #e1e5eb',
              display: 'flex',
              flexDirection: 'column',
              background: '#fafbfc',
            }}>
              <div style={{
                padding: 12,
                borderBottom: '1px solid #e1e5eb',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: '#fff',
              }}>
                <SearchCheck size={16} style={{ color: '#6699CC' }} />
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Tìm trong tin nhắn</span>
                <Button
                  type="text"
                  size="small"
                  icon={<X size={14} />}
                  onClick={() => {
                    setSearchPanelOpen(false);
                    setMessageSearchQuery('');
                  }}
                />
              </div>
              <div style={{ padding: 12 }}>
                <Input
                  prefix={<Search size={14} style={{ color: '#8a95a5' }} />}
                  placeholder="Nhập từ khóa..."
                  value={messageSearchQuery}
                  onChange={(e) => setMessageSearchQuery(e.target.value)}
                  allowClear
                  autoFocus
                />
                <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 6 }}>
                  {messageSearchQuery.trim() === ''
                    ? 'Nhập từ khóa để tìm tin nhắn trong phòng này'
                    : searchLoading
                    ? 'Đang tìm...'
                    : `${searchResults.length} kết quả`}
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {searchResults.length === 0 && messageSearchQuery.trim() && !searchLoading && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#8a95a5', fontSize: 12 }}>
                    Không tìm thấy tin nhắn nào chứa "{messageSearchQuery}"
                  </div>
                )}
                {searchResults.map(msg => (
                  <SearchResultItem
                    key={msg.id}
                    msg={msg}
                    query={messageSearchQuery}
                    isMine={msg.senderId === currentUser?.id}
                    onClick={() => {
                      // Nếu tin có trong messages hiện tại → scroll + highlight
                      const exists = messages.some(m => m.id === msg.id);
                      if (exists) {
                        setHighlightMessageId(msg.id);
                      } else {
                        // Tin cũ chưa load (beyond 50 gần nhất) → thông báo
                        message.info('Tin này ngoài phạm vi 50 tin gần nhất. Cuộn lên để xem thêm.');
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {createModalOpen === 'direct' && (
        <CreateDirectModal
          onClose={() => setCreateModalOpen(null)}
          onCreated={(room) => {
            qc.invalidateQueries({ queryKey: ['chat-rooms'] });
            setCreateModalOpen(null);
            navigate(`/chat/${room.id}`);
          }}
        />
      )}
      {createModalOpen === 'group' && (
        <CreateGroupModal
          onClose={() => setCreateModalOpen(null)}
          onCreated={(room) => {
            qc.invalidateQueries({ queryKey: ['chat-rooms'] });
            setCreateModalOpen(null);
            navigate(`/chat/${room.id}`);
          }}
        />
      )}
    </>
  );
}

// ============================================================
// Room item trong sidebar
// ============================================================
function RoomItem({ room, active, currentUserId, onClick }: {
  room: ChatRoom; active: boolean; currentUserId?: string; onClick: () => void;
}) {
  const displayName = getRoomDisplayName(room, currentUserId);
  const initials = displayName.split(' ').slice(-1)[0]?.[0]?.toUpperCase() ?? '?';

  return (
    <div
      onClick={onClick}
      style={{
        padding: 12,
        borderBottom: '1px solid #eef0f3',
        cursor: 'pointer',
        background: active ? '#e8f0fa' : 'transparent',
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f0f3f7'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <Badge count={room.unreadCount} size="small" offset={[-2, 2]}>
        <Avatar size={40} style={{
          background: room.type === 'GROUP' ? '#6699CC' : '#85b2dd',
          fontWeight: 600,
        }}>
          {room.type === 'GROUP' ? <Users size={18} /> : initials}
        </Avatar>
      </Badge>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 6,
          marginBottom: 2,
        }}>
          <span style={{
            fontWeight: room.unreadCount > 0 ? 600 : 500,
            fontSize: 13,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {displayName}
          </span>
          <span style={{ fontSize: 10, color: '#8a95a5', whiteSpace: 'nowrap' }}>
            {room.lastMessageAt ? formatDistanceToNow(room.lastMessageAt) : ''}
          </span>
        </div>
        <div style={{
          fontSize: 12,
          color: room.unreadCount > 0 ? '#2d3748' : '#7d8a98',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: room.unreadCount > 0 ? 500 : 400,
        }}>
          {room.lastMessagePreview ?? <i>Chưa có tin nhắn</i>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Chat header - tên phòng + actions
// ============================================================
function ChatHeader({ room, currentUserId, onBack, onToggleSearch, searchActive }: {
  room: ChatRoom; currentUserId?: string; onBack: () => void;
  onToggleSearch?: () => void; searchActive?: boolean;
}) {
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const displayName = getRoomDisplayName(room, currentUserId);
  const isAdmin = room.members.find(m => m.userId === currentUserId)?.role === 'ADMIN';

  const handleLeave = async () => {
    Modal.confirm({
      title: 'Rời nhóm?',
      content: `Bạn có chắc muốn rời "${displayName}"?`,
      okText: 'Rời nhóm',
      cancelText: 'Hủy',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await chatService.leaveRoom(room.id);
          message.success('Đã rời nhóm');
          qc.invalidateQueries({ queryKey: ['chat-rooms'] });
          navigate('/chat');
        } catch (e: any) {
          message.error(e.response?.data?.message ?? 'Lỗi');
        }
      },
    });
  };

  const handleArchive = async () => {
    try {
      await chatService.archiveRoom(room.id);
      message.success('Đã lưu trữ');
      qc.invalidateQueries({ queryKey: ['chat-rooms'] });
      navigate('/chat');
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi');
    }
  };

  return (
    <div style={{
      padding: 12,
      borderBottom: '1px solid #e1e5eb',
      background: '#fff',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      <Button type="text" icon={<ArrowLeft size={16} />} onClick={onBack} />
      <Avatar size={36} style={{
        background: room.type === 'GROUP' ? '#6699CC' : '#85b2dd',
      }}>
        {room.type === 'GROUP' ? <Users size={16} /> : displayName[0]?.toUpperCase()}
      </Avatar>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{displayName}</div>
        <div style={{ fontSize: 11, color: '#7d8a98' }}>
          {room.type === 'GROUP'
            ? `${room.members.length} thành viên`
            : room.members.find(m => m.userId !== currentUserId)?.position ?? ''}
        </div>
      </div>
      {onToggleSearch && (
        <Tooltip title="Tìm trong tin nhắn">
          <Button
            type={searchActive ? 'primary' : 'text'}
            icon={<Search size={16} />}
            onClick={onToggleSearch}
          />
        </Tooltip>
      )}
      {room.type === 'GROUP' && (
        <Dropdown
          menu={{
            items: [
              ...(isAdmin ? [
                { key: 'archive', label: 'Lưu trữ nhóm', icon: <Trash2 size={14} />, onClick: handleArchive, danger: true },
              ] : []),
              { key: 'leave', label: 'Rời nhóm', icon: <LogOut size={14} />, onClick: handleLeave, danger: true },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreVertical size={16} />} />
        </Dropdown>
      )}
    </div>
  );
}

// ============================================================
// Message bubble
// ============================================================
function MessageBubble({ msg, isMine, showAvatar, onReply, onPin, onUnpin, canPin, highlighted, innerRef }: {
  msg: ChatMessage; isMine: boolean; showAvatar: boolean;
  onReply?: () => void;
  onPin?: () => void;
  onUnpin?: () => void;
  canPin?: boolean;
  highlighted?: boolean;
  innerRef?: (el: HTMLDivElement | null) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const { message: toast } = AntdApp.useApp();

  const handleDownload = async () => {
    try {
      const blob = await chatService.downloadAttachment(msg.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = msg.attachmentName ?? 'file';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error('Không tải được file');
    }
  };

  // Highlight @mention trong content
  const renderContent = (text: string) => {
    const parts = text.split(/(@[\wÀ-ỹ\s]+?(?=\s|$))/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return (
          <span key={i} style={{
            color: isMine ? '#cce4ff' : '#0066cc',
            fontWeight: 600,
            background: isMine ? 'rgba(255,255,255,0.15)' : 'rgba(102,153,204,0.1)',
            padding: '1px 4px',
            borderRadius: 3,
          }}>
            {part}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  const isImage = msg.attachmentMime?.startsWith('image/');

  return (
    <div
      ref={innerRef}
      style={{
        display: 'flex',
        flexDirection: isMine ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 8,
        marginBottom: 8,
        padding: highlighted ? 6 : 0,
        margin: highlighted ? '-6px 0 2px' : '0 0 8px',
        background: highlighted ? 'rgba(250, 219, 20, 0.25)' : 'transparent',
        borderRadius: 8,
        transition: 'background 0.4s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ width: 32, flexShrink: 0 }}>
        {showAvatar && !isMine && (
          <Avatar size={32} style={{ background: '#85b2dd' }}>
            {msg.senderName?.split(' ').slice(-1)[0]?.[0]?.toUpperCase() ?? '?'}
          </Avatar>
        )}
      </div>
      <div style={{ maxWidth: '70%', minWidth: 0 }}>
        {showAvatar && !isMine && (
          <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 2, paddingLeft: 10 }}>
            {msg.senderName}
          </div>
        )}

        {/* Reply preview */}
        {msg.replyToId && msg.replyToContent && (
          <div style={{
            fontSize: 11,
            padding: '6px 10px',
            borderLeft: `3px solid ${isMine ? '#6699CC' : '#85b2dd'}`,
            background: '#f0f3f7',
            borderRadius: '6px 6px 0 0',
            maxWidth: '100%',
          }}>
            <div style={{ color: '#6699CC', fontWeight: 600 }}>{msg.replyToSenderName}</div>
            <div style={{
              color: '#7d8a98',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {msg.replyToContent.slice(0, 80)}
            </div>
          </div>
        )}

        {/* Attachment */}
        {msg.attachmentUrl && !msg.isDeleted && (
          <div style={{
            background: isMine ? '#6699CC' : '#fff',
            color: isMine ? '#fff' : '#1a1d24',
            padding: isImage ? 4 : 8,
            borderRadius: 8,
            border: isMine ? 'none' : '1px solid #e1e5eb',
            marginBottom: msg.content && msg.content !== `📎 ${msg.attachmentName}` ? 4 : 0,
          }}>
            {isImage ? (
              <img
                src={`data:${msg.attachmentMime};base64,`}
                alt={msg.attachmentName}
                style={{ maxWidth: 260, maxHeight: 200, borderRadius: 4, cursor: 'pointer' }}
                onClick={handleDownload}
                onError={(e) => {
                  // Ảnh không có base64 preview, fallback: click download
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : null}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                padding: 6,
              }}
              onClick={handleDownload}
            >
              <Paperclip size={14} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {msg.attachmentName}
                </div>
                <div style={{ fontSize: 10, opacity: 0.8 }}>
                  {msg.attachmentSize ? `${(msg.attachmentSize / 1024).toFixed(1)} KB` : ''}
                </div>
              </div>
              <Download size={14} />
            </div>
          </div>
        )}

        {/* Text content */}
        {msg.content && (!msg.attachmentUrl || msg.content !== `📎 ${msg.attachmentName}`) && (
          <div style={{
            background: isMine ? '#6699CC' : '#fff',
            color: isMine ? '#fff' : '#1a1d24',
            padding: '8px 12px',
            borderRadius: 12,
            border: isMine ? 'none' : '1px solid #e1e5eb',
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontStyle: msg.isDeleted ? 'italic' : 'normal',
            opacity: msg.isDeleted ? 0.6 : 1,
            position: 'relative',
          }}>
            {msg.isDeleted ? msg.content : renderContent(msg.content)}

            {/* Pinned indicator nhỏ trong bubble */}
            {msg.pinnedAt && !msg.isDeleted && (
              <div style={{
                fontSize: 10,
                marginTop: 4,
                opacity: 0.7,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}>
                <Pin size={10} /> Đã ghim
              </div>
            )}

            {/* Hover actions */}
            {hovered && !msg.isDeleted && (
              <div style={{
                position: 'absolute',
                top: -16,
                [isMine ? 'left' : 'right']: -40,
                background: '#fff',
                borderRadius: 6,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                padding: 2,
                display: 'flex',
                gap: 2,
              }}>
                {onReply && (
                  <Tooltip title="Trả lời">
                    <Button
                      type="text"
                      size="small"
                      icon={<Reply size={13} />}
                      onClick={onReply}
                    />
                  </Tooltip>
                )}
                {canPin && !msg.pinnedAt && onPin && (
                  <Tooltip title="Ghim tin">
                    <Button
                      type="text"
                      size="small"
                      icon={<Pin size={13} />}
                      onClick={onPin}
                    />
                  </Tooltip>
                )}
                {canPin && msg.pinnedAt && onUnpin && (
                  <Tooltip title="Gỡ ghim">
                    <Button
                      type="text"
                      size="small"
                      icon={<PinOff size={13} />}
                      onClick={onUnpin}
                    />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{
          fontSize: 10,
          color: '#8a95a5',
          marginTop: 2,
          textAlign: isMine ? 'right' : 'left',
          paddingLeft: isMine ? 0 : 10,
          paddingRight: isMine ? 10 : 0,
        }}>
          {formatShortDateTime(msg.createdAt)}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// @mention dropdown
// ============================================================
function MentionDropdown({ query, members, onPick }: {
  query: string;
  members: { userId: string; fullName: string; position?: string }[];
  onPick: (m: { userId: string; fullName: string }) => void;
}) {
  const q = query.toLowerCase();
  const filtered = query.trim()
    ? members.filter(m => m.fullName.toLowerCase().includes(q))
    : members;
  if (filtered.length === 0) return null;

  return (
    <div style={{
      position: 'absolute',
      bottom: '100%',
      left: 0,
      right: 0,
      background: '#fff',
      border: '1px solid #e1e5eb',
      borderRadius: 6,
      boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
      maxHeight: 200,
      overflowY: 'auto',
      marginBottom: 4,
      zIndex: 10,
    }}>
      {filtered.slice(0, 8).map(m => (
        <div
          key={m.userId}
          onClick={() => onPick(m)}
          style={{
            padding: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderBottom: '1px solid #f0f3f7',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f7fa')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <Avatar size={24} style={{ background: '#85b2dd', fontSize: 11 }}>
            {m.fullName.split(' ').slice(-1)[0][0]?.toUpperCase()}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{m.fullName}</div>
            {m.position && <div style={{ fontSize: 11, color: '#8a95a5' }}>{m.position}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Create modals
// ============================================================
function CreateDirectModal({ onClose, onCreated }: {
  onClose: () => void; onCreated: (r: ChatRoom) => void;
}) {
  const [form] = Form.useForm();
  const { message } = AntdApp.useApp();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const currentUser = useAuthStore((s) => s.user);

  useEffect(() => {
    setLoading(true);
    userService.list({ pageSize: 200 })
      .then(r => setUsers(r.items.filter(u => u.id !== currentUser?.id)))
      .catch(() => message.error('Không tải được danh sách user'))
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      const room = await chatService.createDirect(values.otherUserId);
      onCreated(room);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi');
    } finally { setCreating(false); }
  };

  return (
    <Modal title="Chat với người..." open onCancel={onClose} onOk={submit} confirmLoading={creating} okText="Bắt đầu" cancelText="Hủy">
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="otherUserId" label="Chọn người" rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Tìm theo tên..."
            loading={loading}
            options={users.map(u => ({
              value: u.id,
              label: `${u.fullName} — ${u.position ?? ''} (${u.department?.name ?? ''})`,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function CreateGroupModal({ onClose, onCreated }: {
  onClose: () => void; onCreated: (r: ChatRoom) => void;
}) {
  const [form] = Form.useForm();
  const { message } = AntdApp.useApp();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const currentUser = useAuthStore((s) => s.user);

  useEffect(() => {
    setLoading(true);
    userService.list({ pageSize: 200 })
      .then(r => setUsers(r.items.filter(u => u.id !== currentUser?.id)))
      .catch(() => message.error('Không tải được danh sách user'))
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      const room = await chatService.createGroup({
        name: values.name,
        description: values.description,
        memberIds: values.memberIds,
      });
      onCreated(room);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi');
    } finally { setCreating(false); }
  };

  return (
    <Modal title="Tạo nhóm chat" open onCancel={onClose} onOk={submit} confirmLoading={creating} okText="Tạo nhóm" cancelText="Hủy" width={560}>
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="name" label="Tên nhóm" rules={[{ required: true, message: 'Bắt buộc' }]}>
          <Input placeholder="VD: Team CNTT" />
        </Form.Item>
        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={2} placeholder="Mục đích nhóm" />
        </Form.Item>
        <Form.Item name="memberIds" label="Thành viên" rules={[{ required: true, message: 'Chọn ít nhất 1 thành viên' }]}>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="Chọn thành viên..."
            loading={loading}
            options={users.map(u => ({
              value: u.id,
              label: `${u.fullName} — ${u.department?.name ?? ''}`,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ============================================================
// Helpers
// ============================================================
function getRoomDisplayName(room: ChatRoom, currentUserId?: string): string {
  if (room.type === 'GROUP') return room.name ?? '(Không tên)';
  // DIRECT: lấy tên của đối phương
  const other = room.members.find(m => m.userId !== currentUserId);
  return other?.fullName ?? '(Không rõ)';
}

/**
 * Logic FE mirror backend: quyết định user hiện tại có được pin/unpin tin này không.
 * - DIRECT: cả 2 bên đều pin/unpin được
 * - GROUP: admin hoặc người gửi được pin; admin hoặc người đã pin được unpin
 */
function canPinMessage(msg: ChatMessage, currentUserId: string | undefined, room: ChatRoom | undefined): boolean {
  if (!currentUserId || !room) return false;
  if (msg.isDeleted) return false;
  if (room.type === 'DIRECT') return true;
  // GROUP
  const myRole = room.members.find(m => m.userId === currentUserId)?.role;
  if (myRole === 'ADMIN') return true;
  // Pin: sender được pin. Unpin: người pin được unpin.
  if (!msg.pinnedAt) {
    return msg.senderId === currentUserId;
  }
  return msg.pinnedBy === currentUserId;
}

// ============================================================
// PinnedBanner - hiện danh sách tin ghim trên đầu phòng
// ============================================================
function PinnedBanner({ messages, expanded, onToggle, currentUserId, roomType, myRole, onJumpTo, onUnpin }: {
  messages: ChatMessage[];
  expanded: boolean;
  onToggle: () => void;
  currentUserId?: string;
  roomType: 'DIRECT' | 'GROUP';
  myRole?: 'ADMIN' | 'MEMBER';
  onJumpTo: (id: string) => void;
  onUnpin: (id: string) => void;
}) {
  if (messages.length === 0) return null;

  const canUnpin = (msg: ChatMessage): boolean => {
    if (roomType === 'DIRECT') return true;
    if (myRole === 'ADMIN') return true;
    return msg.pinnedBy === currentUserId;
  };

  return (
    <div style={{
      background: '#fffbe6',
      borderBottom: '1px solid #ffe58f',
    }}>
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          padding: '8px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Pin size={14} style={{ color: '#d48806' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#874d00', flex: 1 }}>
          {messages.length === 1
            ? '1 tin đã ghim'
            : `${messages.length} tin đã ghim`}
        </span>
        {expanded
          ? <ChevronUp size={14} style={{ color: '#d48806' }} />
          : <ChevronDown size={14} style={{ color: '#d48806' }} />
        }
      </div>

      {/* List */}
      <div>
        {(expanded ? messages : messages.slice(0, 1)).map((msg) => (
          <div
            key={msg.id}
            style={{
              padding: '8px 14px',
              borderTop: '1px solid #ffe58f',
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
              onClick={() => onJumpTo(msg.id)}
            >
              <div style={{
                fontSize: 11,
                fontWeight: 600,
                color: '#874d00',
                marginBottom: 2,
              }}>
                {msg.senderName} · {formatShortDateTime(msg.createdAt)}
              </div>
              <div style={{
                fontSize: 12,
                color: '#48535e',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: expanded ? 'normal' : 'nowrap',
                lineHeight: 1.4,
              }}>
                {msg.attachmentName && (
                  <span style={{ marginRight: 6, color: '#6699CC' }}>
                    <Paperclip size={11} style={{ verticalAlign: -2 }} /> {msg.attachmentName}
                  </span>
                )}
                {msg.content && msg.content !== `📎 ${msg.attachmentName}` ? msg.content : null}
              </div>
            </div>
            {canUnpin(msg) && (
              <Tooltip title="Gỡ ghim">
                <Button
                  type="text"
                  size="small"
                  icon={<PinOff size={13} style={{ color: '#d48806' }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnpin(msg.id);
                  }}
                />
              </Tooltip>
            )}
          </div>
        ))}

        {!expanded && messages.length > 1 && (
          <div
            onClick={onToggle}
            style={{
              padding: '4px 14px 8px',
              fontSize: 11,
              color: '#d48806',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            Xem thêm {messages.length - 1} tin đã ghim...
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Search result item trong search panel
// ============================================================
function SearchResultItem({ msg, query, isMine, onClick }: {
  msg: ChatMessage; query: string; isMine: boolean; onClick: () => void;
}) {
  // Highlight từ khóa trong preview
  const highlightText = (text: string, q: string) => {
    if (!q.trim()) return text;
    const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase() ? (
        <mark key={i} style={{ background: '#fadb14', padding: '0 2px', borderRadius: 2 }}>
          {part}
        </mark>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  // Cắt snippet xung quanh keyword để hiện gọn
  const makeSnippet = (content: string, q: string): string => {
    if (!q.trim() || content.length <= 100) return content;
    const idx = content.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return content.slice(0, 100) + '...';
    const start = Math.max(0, idx - 30);
    const end = Math.min(content.length, idx + q.length + 60);
    return (start > 0 ? '...' : '') + content.slice(start, end) + (end < content.length ? '...' : '');
  };

  return (
    <div
      onClick={onClick}
      style={{
        padding: 10,
        borderBottom: '1px solid #eef0f3',
        cursor: 'pointer',
        fontSize: 12,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f7fa')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 3,
      }}>
        <span style={{ fontWeight: 600, color: isMine ? '#6699CC' : '#2d3748' }}>
          {isMine ? 'Bạn' : msg.senderName}
        </span>
        <span style={{ fontSize: 10, color: '#8a95a5' }}>
          {formatShortDateTime(msg.createdAt)}
        </span>
      </div>
      <div style={{ color: '#48535e', lineHeight: 1.4, wordBreak: 'break-word' }}>
        {highlightText(makeSnippet(msg.content, query), query)}
      </div>
      {msg.attachmentName && (
        <div style={{ fontSize: 10, color: '#6699CC', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Paperclip size={10} /> {msg.attachmentName}
        </div>
      )}
    </div>
  );
}
